/**
 * Address display helpers.
 *
 * A deliberate mirror of `formatPostalCodeWithCountry` in `libs/ui` (which admin and manager use)
 * and of `@monrepo-build/db`'s server-side original. The warehouse app has no `@ui` alias and does
 * not depend on `@monrepo-build/ui` — `validationUtils.ts` in this same folder is mirrored from
 * libs/ui for exactly that reason, so this follows the established pattern rather than pulling a
 * Radix/Tailwind component library into a Capacitor bundle for two lines of formatting.
 *
 * The three copies MUST agree: the whole point is that one address reads identically on a
 * warehouse screen, an admin card and the T3/T4 generated from it.
 */

/** ISO 3166-1 alpha-2, upper-cased and trimmed; anything missing or malformed is US. */
const normalizeCountryCode = (countryCode?: string | null): string => {
  const trimmed = (countryCode ?? '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(trimmed) ? trimmed : 'US';
};

/**
 * "Postal code + country", e.g. `02108 US`. The country is appended for US addresses too, so a
 * reader can tell "domestic" from "nobody filled it in". Returns the country alone when there is
 * no postal code.
 */
export const formatPostalCodeWithCountry = (
  zipCode?: string | null,
  countryCode?: string | null
): string => {
  const postal = (zipCode ?? '').trim();
  const country = normalizeCountryCode(countryCode);
  return postal ? `${postal} ${country}` : country;
};
